import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Chip,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import dayjs from "dayjs";
import { LadderChip, normalizeLadder } from "../FormElements/LadderField";

const getStatus = (item) => {
  const start = item.availabilityStartTime
    ? dayjs(item.availabilityStartTime)
    : null;
  if (start && start.isAfter(dayjs())) {
    return {
      label: `Scheduled · ${start.format("DD MMM, h:mm A")}`,
      color: "warning",
    };
  }
  return { label: "Active", color: "success" };
};

const moveItem = (list, from, to) => {
  const next = [...list];
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
};

/**
 * Drag-to-reorder dialog for the order mobile users see karaoke songs /
 * dance tracks in. Lists scheduled + active tracks (TBD and expired are
 * hidden from users, so they have no position).
 *
 * @param {() => Promise<object[]>} fetchTracks resolves to tracks in current display order
 * @param {(ids: string[]) => Promise<void>} saveOrder
 * @param {(track: object) => string} [getSubtitle]
 */
const TrackOrderModal = ({
  open,
  onClose,
  onSuccess,
  title,
  fetchTracks,
  saveOrder,
  getSubtitle,
}) => {
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [dragIndex, setDragIndex] = useState(null);
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setIsDirty(false);
    fetchTracks()
      .then((list) => !cancelled && setTracks(list || []))
      .catch((err) => {
        if (cancelled) return;
        setTracks([]);
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "Failed to load tracks"
        );
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [open]);

  const move = (from, to) => {
    if (to < 0 || to >= tracks.length || from === to) return;
    setTracks((list) => moveItem(list, from, to));
    setIsDirty(true);
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === index) return;
    move(dragIndex, index);
    setDragIndex(index);
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      setError("");
      await saveOrder(tracks.map((track) => track._id));
      onSuccess?.();
      onClose();
    } catch (err) {
      setError(
        err?.response?.data?.message || err?.message || "Failed to save order"
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { borderRadius: "12px" } }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0",
          pb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight={600} color="#142C5C">
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Drag to set the order mobile users see. Scheduled tracks take their
            spot once they go live. New uploads appear after these.
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2, minHeight: 200 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={28} sx={{ color: "#142C5C" }} />
          </Box>
        ) : tracks.length === 0 ? (
          <Typography sx={{ textAlign: "center", py: 6, color: "#999" }}>
            No scheduled or active tracks to order
          </Typography>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, pt: 1 }}>
            {tracks.map((track, index) => {
              const status = getStatus(track);
              const forFun = normalizeLadder(track.ladder) === "for_fun";
              return (
                <Box
                  key={track._id}
                  draggable
                  onDragStart={() => setDragIndex(index)}
                  onDragOver={(e) => handleDragOver(e, index)}
                  onDragEnd={() => setDragIndex(null)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    p: 1,
                    borderTop: "1px solid #e0e0e0",
                    borderRight: "1px solid #e0e0e0",
                    borderBottom: "1px solid #e0e0e0",
                    borderLeft: `4px solid ${forFun ? "#0288d1" : "#ed6c02"}`,
                    borderRadius: "8px",
                    backgroundColor:
                      dragIndex === index ? "rgba(20, 44, 92, 0.08)" : "#fff",
                    cursor: "grab",
                  }}
                >
                  <DragIndicatorIcon sx={{ color: "#999" }} />
                  <Typography
                    sx={{ fontWeight: 600, minWidth: 24, color: "#142C5C" }}
                  >
                    {index + 1}
                  </Typography>
                  <img
                    src={track.coverImage}
                    alt=""
                    style={{
                      width: 40,
                      height: 40,
                      objectFit: "cover",
                      borderRadius: 4,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      noWrap
                      sx={{ fontSize: "14px", fontWeight: 500 }}
                    >
                      {track.title}
                    </Typography>
                    {getSubtitle && (
                      <Typography
                        noWrap
                        variant="caption"
                        color="text.secondary"
                      >
                        {getSubtitle(track)}
                      </Typography>
                    )}
                  </Box>
                  <LadderChip ladder={track.ladder} sx={{ flexShrink: 0 }} />
                  <Chip
                    label={status.label}
                    color={status.color}
                    size="small"
                    sx={{ fontWeight: 500, fontSize: "11px", flexShrink: 0 }}
                  />
                  <Box sx={{ display: "flex", flexDirection: "column" }}>
                    <Tooltip title="Move up">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => move(index, index - 1)}
                          disabled={index === 0}
                          sx={{ padding: "2px" }}
                        >
                          <ArrowUpwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move down">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => move(index, index + 1)}
                          disabled={index === tracks.length - 1}
                          sx={{ padding: "2px" }}
                        >
                          <ArrowDownwardIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
        {error && (
          <Typography color="error" variant="body2" sx={{ mt: 2 }}>
            {error}
          </Typography>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2, gap: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderColor: "#142C5C",
            color: "#142C5C",
            borderRadius: "6px",
            "&:hover": {
              borderColor: "#0f2247",
              backgroundColor: "transparent",
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={!isDirty || saving || loading}
          sx={{
            backgroundColor: "#142C5C",
            color: "#fff",
            borderRadius: "6px",
            "&:hover": { backgroundColor: "#0f2247" },
          }}
        >
          {saving ? "Saving..." : "Save Order"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrackOrderModal;
